/**
 * Centralized Quote Calculation Utility
 *
 * This utility provides consistent quote total calculations across the application.
 * Used in QuoteSummary, QuoteConfirmation, and quote creation flow.
 *
 * IMPORTANT: All monetary values are rounded to 2 decimal places at each calculation step
 * to ensure consistency and avoid floating-point precision issues.
 *
 * NOTE: If vssDiscountPercentage is 0, it means the VSS discount has already been applied
 * to the product prices in the configuration (via applyMarginToConfiguration), so we should
 * not calculate it separately.
 */

/**
 * Round a number to 2 decimal places (standard for currency)
 * Uses Math.round to ensure proper rounding (not truncation)
 */
export const roundToTwoDecimals = (value: number): number => {
  return Math.round((value + Number.EPSILON) * 100) / 100;
};

interface CartItem {
  price: {
    totalPrice: number;
    basePrice?: number;
    vssBasePrice?: number;
    additionalCharges?: Array<{
      vssbase?: number;
      price?: number;
      [key: string]: any;
    }>;
  };
  quantity: number;
}

interface CalculateQuoteTotalsParams {
  cartItems: CartItem[];
  vssDiscountPercentage: number;
  customDiscountPercentage: number;
  customDiscountAmount?: number;
  discountType?: 'percentage' | 'amount';
  /** Customer VAT % */
  vatPercentage: number;
}

interface QuoteTotals {
  subtotal: number;
  vssDiscountAmount: number;
  customDiscountAmount: number;
  totalDiscountAmount: number;
  subtotalAfterDiscount: number;
  vat: number;
  totalPrice: number;
}

/**
 * Calculate quote totals with VSS and custom discounts
 *
 * @param params - Cart items and discount percentages/amounts
 * @returns Calculated totals including all discounts and VAT
 */
export const calculateQuoteTotals = ({
  cartItems,
  vssDiscountPercentage,
  customDiscountPercentage,
  customDiscountAmount: customDiscountAmountParam = 0,
  discountType = 'percentage',
  vatPercentage,
}: CalculateQuoteTotalsParams): QuoteTotals => {
  // Calculate subtotal from current prices (rounded at line item level)
  const subtotal = roundToTwoDecimals(
    cartItems.reduce((sum, item) => {
      const lineTotal = roundToTwoDecimals(item.price.totalPrice * item.quantity);
      return sum + lineTotal;
    }, 0)
  );

  // Calculate VSS discount amount based on subtotal (rounded)
  const vssDiscountAmount = roundToTwoDecimals(subtotal * (vssDiscountPercentage / 100));

  // Calculate custom discount amount based on type (rounded)
  const customDiscountAmount = discountType === 'amount'
    ? roundToTwoDecimals(customDiscountAmountParam)
    : roundToTwoDecimals(subtotal * (customDiscountPercentage / 100));

  // Total discount (rounded)
  const totalDiscountAmount = roundToTwoDecimals(vssDiscountAmount + customDiscountAmount);

  // Subtract total discount from subtotal (rounded)
  const subtotalAfterDiscount = roundToTwoDecimals(subtotal - totalDiscountAmount);

  // Calculate VAT on discounted subtotal using customer vatPercentage (rounded)
  const vat = roundToTwoDecimals(subtotalAfterDiscount * (vatPercentage / 100));

  // Calculate final total (rounded)
  const totalPrice = roundToTwoDecimals(subtotalAfterDiscount + vat);

  return {
    subtotal,
    vssDiscountAmount,
    customDiscountAmount,
    totalDiscountAmount,
    subtotalAfterDiscount,
    vat,
    totalPrice,
  };
};
